import { ofType } from 'redux-observable';
import { catchError, mergeMap, filter, mapTo, map } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { endsWith, get } from 'lodash';
import { SessionService, UserService } from 'services';
import { SESSION_EMAIL_KEY } from 'services/SessionService';
import { basicEpic, mappingEpic } from 'redux/ducks/helpers';
import {
  CREATE_SESSION,
  CREATE_SESSION_ERROR,
  CREATE_SESSION_SUCCESS,
  DESTROY_SESSION,
  DESTROY_SESSION_ERROR,
  DESTROY_SESSION_SUCCESS,
  RELOAD_SESSION,
  RELOAD_SESSION_ERROR,
  RELOAD_SESSION_FAILURE,
  RELOAD_SESSION_SUCCESS,
  RELOAD_USER_SUCCESS,
  RELOAD_USER_ERROR,
  UPDATE_SESSION,
  INVALID_SESSION,
  GOOGLE_LOGIN,
  GOOGLE_LOGIN_ERROR,
  GOOGLE_LOGIN_SUCCESS,
  MICROSOFT_LOGIN,
  MICROSOFT_LOGIN_ERROR,
  MICROSOFT_LOGIN_SUCCESS,
  ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH,
  ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH_ERROR,
  ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH_SUCCESS,
} from './index';
import { GET_REFERRAL_HERO_URL, UPDATE_USER_SUCCESS } from 'redux/ducks/User';
import { SHOW_SPINNER, HIDE_SPINNER } from 'redux/ducks/LoadingSpinner';
import { Observable } from 'rxjs';
import ReferralService from 'services/ReferralService';
import { EXPIRED_SESSION_ERROR_MESSAGE } from 'components/PrivateRoute/PrivateRoute';
import { getReferralCodeWithFallback } from '@helpers/referralHero';
import { ERROR_FLASH_MESSAGE } from '../Flash';

// Constants
// ========================================================
const STORAGE_KEY = 'session';
const sessionService = SessionService ? new SessionService() : null;

// Epics
// ========================================================
export const createSessionEpic = action$ => {
  return action$.pipe(
    ofType(CREATE_SESSION),
    mergeMap(action => {
      window.localStorage.clear();
      window.sessionStorage.clear();

      const history = action.payload.history;
      return sessionService
        .login(action.payload)
        .then(payload => {
          payload.session.rememberMe = action.payload.rememberMe;
          const stringPayload = JSON.stringify(payload.session);

          if (action.payload.rememberMe) {
            window.localStorage.setItem(STORAGE_KEY, stringPayload);
            window.localStorage.setItem(SESSION_EMAIL_KEY, payload.session.uid);
          } else {
            window.sessionStorage.setItem(STORAGE_KEY, stringPayload);
          }

          return {
            type: CREATE_SESSION_SUCCESS,
            payload: {
              ...payload,
              history,
              from: action.payload.from,
              step: action.payload.step,
              callbackPayload: action.payload.callbackPayload,
            },
          };
        })
        .catch(error => {
          const response = error.response || {};

          const status = response.status ?? null;
          const headers = response.headers ?? null;

          let message = 'Something went wrong';

          // Check for specific backend error messages first
          if (error.response && error.response.data) {
            const backendMessage = error.response.data.message;
            const backendError = error.response.data.error;

            // If backend provides a specific message, use it
            if (backendMessage && typeof backendMessage === 'string') {
              message = backendMessage;
            }
            // If backend provides a specific error key, map it to user-friendly message
            else if (backendError) {
              switch (backendError) {
                case 'email_already_registered_as_patient':
                  message =
                    'This email is already registered as a Patient. Please use the Patient login page.';
                  break;
                case 'email_already_registered_as_parent':
                  message = 'This email is already registered as a Parent. Please use the Parent login page.';
                  break;
                case 'email_already_registered_as_athletic_trainer':
                  message =
                    'This email is already registered as an Athletic Trainer. Please use the Athletic Trainer login page.';
                  break;
                default:
                  break;
              }
            }
          }

          // Fallback to existing error handling logic
          if (status === 403 && !message) {
            if (headers && headers['location']) {
              const url = new URL(headers['location']);
              const role = url.pathname.split('/').pop();

              if (role === 'athletic-trainer') {
                message = 'Please login as a Job Poster';
              } else {
                history.push(url.pathname);
                message = 'Please login via this other portal';
              }
            } else {
              message = 'Please login via this other portal';
            }
          } else if ([401, 404].includes(status) && !message) {
            message = 'Invalid email or password';
          } else if (status === 500 && !message) {
            message = 'Something went wrong';
          }

          return {
            type: CREATE_SESSION_ERROR,
            payload: {
              ...error,
              message,
            },
          };
        });
    })
  );
};

export const progressUserToEAPAfterSignUp = action$ => {
  return action$.pipe(
    ofType(CREATE_SESSION_SUCCESS),
    filter(action => action.payload?.step === 'event-intro'),
    mergeMap(action => {
      const { history } = action.payload;

      history.push('/v2/create-event/organization');
      return Observable.empty();
    })
  );
};

export const getReferralHeroUrlEpic = basicEpic(GET_REFERRAL_HERO_URL, (_, state) => {
  return new ReferralService(state.session).getReferralUrl();
});

export const destroySessionEpic = (action$, store) => {
  return action$.pipe(
    ofType(DESTROY_SESSION),
    mergeMap(action => {
      const session = store.getState().session;
      window.localStorage.clear();
      window.sessionStorage.clear();

      if (session.rememberMe) {
        window.localStorage.setItem(SESSION_EMAIL_KEY, session.uid);
      }

      return sessionService
        .logout(session)
        .then(() => {
          return { type: DESTROY_SESSION_SUCCESS, payload: action.payload };
        })
        .catch(error => ({
          type: DESTROY_SESSION_ERROR,
          payload: error,
        }));
    })
  );
};

export const reloadSessionEpic = action$ => {
  return action$.pipe(
    ofType(RELOAD_SESSION),
    mergeMap(() => {
      const localStorage = window.localStorage.getItem(STORAGE_KEY);
      const sessionStorage = window.sessionStorage.getItem(STORAGE_KEY);

      let session = localStorage ? localStorage : sessionStorage;

      if (session) {
        return of({
          type: RELOAD_SESSION_SUCCESS,
          payload: JSON.parse(session),
        });
      } else {
        return of({ type: RELOAD_SESSION_FAILURE });
      }
    }),
    catchError(error => of({ type: RELOAD_SESSION_ERROR, payload: error }))
  );
};

export const reloadUserInSessionEpic = (action$, store) => {
  return action$.pipe(
    ofType(RELOAD_SESSION_SUCCESS),
    mergeMap(action => {
      const session = get(store.getState(), 'session');
      const currentUser = get(session, 'currentUser');

      return new UserService(session)
        .get(currentUser.id)
        .then(user => ({ type: RELOAD_USER_SUCCESS, payload: user }))
        .catch(error => ({
          type: RELOAD_USER_ERROR,
          payload: error,
        }));
    })
  );
};

export const trackAppVersionEpic = (action$, store) => {
  return action$.pipe(
    ofType(RELOAD_SESSION_SUCCESS, CREATE_SESSION_SUCCESS, GOOGLE_LOGIN_SUCCESS, MICROSOFT_LOGIN_SUCCESS),
    mergeMap(action => {
      const currentUserId = get(store.getState(), 'session.currentUser.id');

      return new Promise(() => {
        if (window._cio) {
          window._cio.identify({
            id: currentUserId,
            app_version: process.env.REACT_APP_VERSION,
          });
        }
      });
    })
  );
};

export const updateUserInSessionEpic = action$ => {
  return action$.pipe(
    ofType(UPDATE_USER_SUCCESS, RELOAD_USER_SUCCESS),
    mergeMap(action => {
      const user = action.payload;
      const localStorage = window.localStorage.getItem(STORAGE_KEY);
      const sessionStorage = window.sessionStorage.getItem(STORAGE_KEY);

      let session = null;
      let storageType = null;

      if (localStorage) {
        session = JSON.parse(localStorage);
        storageType = 'local';
      } else {
        session = JSON.parse(sessionStorage);
        storageType = 'session';
      }

      if (session.currentUser.id === user.id) {
        session.currentUser = user;

        const stringSession = JSON.stringify(session);

        if (storageType === 'local') {
          window.localStorage.setItem(STORAGE_KEY, stringSession);
        } else {
          window.sessionStorage.setItem(STORAGE_KEY, stringSession);
        }

        return of({
          type: UPDATE_SESSION,
          payload: { session: session },
        });
      }
    })
  );
};

export const catchApiErrorsEpic = (action$, store) => {
  return action$.pipe(
    filter(action => action.type !== CREATE_SESSION_ERROR),
    filter(action => action.type !== DESTROY_SESSION_ERROR),
    filter(action => endsWith(action.type, 'ERROR')),
    filter(action => get(action, 'payload.response.status') === 401),
    mapTo({
      type: INVALID_SESSION,
      payload: {
        message: EXPIRED_SESSION_ERROR_MESSAGE,
      },
    })
  );
};

export const showSpinnerEpic = mappingEpic(
  [CREATE_SESSION, DESTROY_SESSION, GOOGLE_LOGIN, MICROSOFT_LOGIN, ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH],
  SHOW_SPINNER
);

export const hideSpinnerEpic = mappingEpic(
  [
    CREATE_SESSION_ERROR,
    DESTROY_SESSION_ERROR,
    DESTROY_SESSION_SUCCESS,
    INVALID_SESSION,
    GOOGLE_LOGIN_SUCCESS,
    GOOGLE_LOGIN_ERROR,
    MICROSOFT_LOGIN_SUCCESS,
    MICROSOFT_LOGIN_ERROR,
    ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH_SUCCESS,
    ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH_ERROR,
  ],
  HIDE_SPINNER
);

export const catchAthleticTrainersOAuthErrorsEpic = (action$, store) => {
  return action$.pipe(
    ofType(ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH_ERROR),
    map(action => {
      return {
        type: ERROR_FLASH_MESSAGE,
        payload: action.payload.message,
      };
    })
  );
};

const handleOAuthError = error => {
  const { status, data } = error.response || {};
  const { error: message } = data;

  let errorMessage = 'Google authentication failed';
  if (status === 401) {
    try {
      errorMessage = data?.errors?.[0] || 'Authentication failed';
    } catch (e) {
      console.error('Error parsing authentication error:', e);
      errorMessage = 'Authentication failed';
    }
  } else if (status === 403) {
    errorMessage = message || 'Access denied';
  } else if (status === 404) {
    errorMessage = 'Authentication endpoint not found';
  } else if (status === 500) {
    errorMessage = 'Server error. Please try again later';
  } else if (message) {
    errorMessage = message;
  }

  return {
    type: ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH_ERROR,
    payload: {
      ...error,
      message: errorMessage,
    },
  };
};

export const handleAthleticTrainerSignUpWithOAuth = (action$, store) => {
  return action$.pipe(
    ofType(ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH),
    mergeMap(action => {
      const { credential, history } = action.payload;
      const referrer = getReferralCodeWithFallback();

      const params = {
        idToken: credential,
        ...store.getState().registration.user,
        ...(referrer && { referral: referrer }),
      };

      return sessionService
        .createAthleticTrainerWithOAuth(params)
        .then(payload => {
          return {
            type: ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH_SUCCESS,
            payload: {
              history,
              ...payload,
            },
          };
        })
        .catch(error => handleOAuthError(error));
    })
  );
};

export const handleAthleticTrainerSignUpWithOAuthSuccessEpic = action$ => {
  return action$.pipe(
    ofType(ATHLETIC_TRAINER_SIGN_UP_WITH_OAUTH_SUCCESS),
    mergeMap(action => {
      const { history } = action.payload;
      history.push('/deeplink-sign-up');
      return Observable.empty();
    })
  );
};

export const googleLoginEpic = basicEpic(GOOGLE_LOGIN, (payload, state) => {
  return sessionService.loginWithGoogle(payload);
});

export const googleLoginSuccessEpic = action$ => {
  return action$.pipe(
    ofType(GOOGLE_LOGIN_SUCCESS),
    mergeMap(action => {
      const payload = action.payload;

      payload.session.rememberMe = false;

      const stringPayload = JSON.stringify(payload.session);

      // Save session to localStorage for OAuth logins
      window.localStorage.setItem(STORAGE_KEY, stringPayload);
      window.localStorage.setItem(SESSION_EMAIL_KEY, payload.session.uid);

      return of({ type: 'GOOGLE_LOGIN_STORAGE_SUCCESS' });
    })
  );
};

export const microsoftLoginEpic = basicEpic(MICROSOFT_LOGIN, (payload, state) => {
  return sessionService.loginWithMicrosoft(payload);
});

export const microsoftLoginSuccessEpic = action$ => {
  return action$.pipe(
    ofType(MICROSOFT_LOGIN_SUCCESS),
    mergeMap(action => {
      const payload = action.payload;

      payload.session.rememberMe = false;

      const stringPayload = JSON.stringify(payload.session);

      // Save session to localStorage for OAuth logins
      window.localStorage.setItem(STORAGE_KEY, stringPayload);
      window.localStorage.setItem(SESSION_EMAIL_KEY, payload.session.uid);

      return of({ type: 'MICROSOFT_LOGIN_STORAGE_SUCCESS' });
    })
  );
};
